import { EPISODE_LABEL_KEYS, type Label } from "@/api";
import { HARDWARE_AXIS_KEYS } from "./hardwareKeys";
import { normalizeLabelText } from "./labelText";

/**
 * 새 라벨 (key, value) 만들기의 판단 로직. 순수 로직만 둔다. 창은 연결만 한다.
 *
 * 여기서 만들 수 있는 것은 **generic 라벨뿐**이다. 예약 축(environment/source/task)은 계약이 정한
 * 전용 컨트롤이 소유한다. 품질은 에피소드 리뷰가 소유하고, 하드웨어 축은 Embodiment 카탈로그가
 * 소유한다.
 * 차단 목록은 호출자가 준다. 화면마다 전용 자리를 가진 축이 다르기 때문이다. 이 모듈은 그
 * 목록을 받아 "왜 안 되는지" 를 문장으로 만든다.
 */

/**
 * 차단된 key 인 이유. 차단이 아니면 null.
 *
 * 축마다 소유자가 달라 사유를 나눈다. 어디로 가야 하는지 알려야 사용자가 다음 행동을 찾는다.
 * 하드웨어 축은 이 저장소가 아는 고정 목록이다. 품질은 에피소드에만 붙는 축이다. 나머지는
 * 호출자가 전달한 목록이라 "전용 컨트롤" 로 묶어 표시한다. 무엇이 전용인지는 화면이 안다.
 */
export function blockedKeyReason(key: string, blockedKeys: readonly string[]): string | null {
  const normalized = normalizeLabelText(key);
  if (normalized === "") {
    return null;
  }
  if (HARDWARE_AXIS_KEYS.includes(normalized)) {
    return "하드웨어 축은 Embodiment 카탈로그가 소유합니다. Embodiment 페이지에서 지정해 주세요.";
  }
  if (!blockedKeys.some((blocked) => normalizeLabelText(blocked) === normalized)) {
    return null;
  }
  if (EPISODE_LABEL_KEYS.includes(normalized)) {
    return "품질과 개입은 에피소드 라벨입니다. 에피소드 리뷰에서 지정해 주세요.";
  }
  return "이 축은 전용 컨트롤이 소유합니다. 이 화면의 해당 카드에서 지정해 주세요.";
}

/**
 * key 자동완성 후보. 카탈로그의 generic key 만 중복 없이 모은다(카탈로그 순서 유지).
 *
 * 차단 축을 후보에서 빼서 사용자가 우연히 고르는 경로를 없앤다. 직접 입력하는 경로는
 * `blockedKeyReason` 이 따로 막는다.
 */
export function genericLabelKeys(
  catalog: readonly Label[],
  blockedKeys: readonly string[],
): string[] {
  const seen = new Set<string>();
  const keys: string[] = [];
  for (const label of catalog) {
    const normalized = normalizeLabelText(label.key);
    if (seen.has(normalized) || blockedKeyReason(label.key, blockedKeys) !== null) {
      continue;
    }
    seen.add(normalized);
    keys.push(label.key);
  }
  return keys;
}

/** 이 (key, value) 가 카탈로그에 이미 있는지 판정한다. 있으면 만들지 않고 고르면 된다. */
export function findExistingLabel(
  catalog: readonly Label[],
  key: string,
  value: string,
): Label | null {
  const normalizedKey = normalizeLabelText(key);
  const normalizedValue = normalizeLabelText(value);
  if (normalizedKey === "" || normalizedValue === "") {
    return null;
  }
  return (
    catalog.find(
      (label) =>
        normalizeLabelText(label.key) === normalizedKey &&
        normalizeLabelText(label.value) === normalizedValue,
    ) ?? null
  );
}

/** 만들기를 막는 사유. null 이면 만들 수 있다. */
export type NewLabelBlocker = { field: "key" | "value"; message: string };

/**
 * 지금 입력으로 만들 수 있는지 판정한다. 막는 첫 사유 하나만 반환한다.
 *
 * 이미 있는 (key,value)도 막는다. 서버는 get-or-create 라 오류를 내지 않는다. 하지만 "만들기" 를
 * 누른 사람은 새 라벨이 생겼다고 이해한다. 실제로는 기존 라벨을 부여한 것이므로, 사용자를 위
 * 검색으로 안내하는 것이 정확하다.
 */
export function newLabelBlocker(
  key: string,
  value: string,
  catalog: readonly Label[],
  blockedKeys: readonly string[],
): NewLabelBlocker | null {
  const reason = blockedKeyReason(key, blockedKeys);
  if (reason !== null) {
    return { field: "key", message: reason };
  }
  if (normalizeLabelText(key) === "") {
    return { field: "key", message: "key를 입력해 주세요." };
  }
  if (normalizeLabelText(value) === "") {
    return { field: "value", message: "값을 입력해 주세요." };
  }
  if (findExistingLabel(catalog, key, value) !== null) {
    return {
      field: "value",
      message: "이미 있는 라벨입니다. 위의 기존 라벨 검색에서 골라 주세요.",
    };
  }
  return null;
}
